import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, ButtonBase, Collapse, Stack, Typography } from '@mui/material';

import type { FilterGroupLayout } from './types';

export type GroupedFilterControl = {
  id: string;
  label: string;
  active: boolean;
  summary?: string;
  node: React.ReactNode;
};

const INITIAL_FIELDS = 3;

export default function GroupedFilterPanel({
  controls,
  groups,
  match,
  onReset,
}: {
  controls: Array<GroupedFilterControl>;
  groups: Array<FilterGroupLayout>;
  match: string;
  onReset?: (ids: Array<string>) => void;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [more, setMore] = useState<Array<string>>([]);
  const searching = Boolean(match.trim());
  const controlMap = new Map(controls.map((control) => [control.id, control]));
  return (
    <Box>
      {groups.map((group) => {
        const allMembers = group.controls.flatMap((id) => (controlMap.has(id) ? [controlMap.get(id)!] : []));
        const members = allMembers.filter((control) =>
          control.label.toLowerCase().includes(match.trim().toLowerCase()),
        );
        if (!members.length) {
          return null;
        }
        const active = allMembers.filter((control) => control.active);
        const open = searching || (expanded[group.id] ?? !group.collapsed);
        const showAll = searching || more.includes(group.id);
        const shown = showAll ? members : members.filter((control, index) => index < INITIAL_FIELDS || control.active);
        const hidden = members.length - shown.length;
        const regionId = `lens-filter-group-${group.id}`;
        return (
          <Box key={group.id} sx={{ borderTop: 1, borderColor: 'divider', py: 0.25 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 36 }}>
              <ButtonBase
                aria-controls={regionId}
                aria-expanded={open}
                onClick={() => setExpanded((current) => ({ ...current, [group.id]: !open }))}
                sx={{ flex: 1, justifyContent: 'flex-start', textAlign: 'left', gap: 0.75, py: 1, minWidth: 0 }}
              >
                <ExpandMoreIcon sx={{ fontSize: 16, flexShrink: 0, transform: open ? undefined : 'rotate(-90deg)' }} />
                <Typography
                  sx={{ fontSize: 12.5, lineHeight: 1.4, fontWeight: 600, flex: 1, overflowWrap: 'break-word' }}
                >
                  {group.label}
                </Typography>
              </ButtonBase>
              {active.length && onReset ? (
                <Button
                  aria-label={`Reset ${group.label} filters`}
                  onClick={() => onReset(allMembers.map((control) => control.id))}
                  size="small"
                  sx={{
                    minWidth: 0,
                    minHeight: 24,
                    px: 0.5,
                    py: 0.25,
                    fontSize: 11,
                    lineHeight: 1.4,
                    textTransform: 'none',
                    flexShrink: 0,
                  }}
                >
                  Reset
                </Button>
              ) : null}
            </Stack>
            {!open && active.length ? (
              <Typography
                sx={{
                  pl: 2.75,
                  pb: 1,
                  fontSize: 11,
                  overflowWrap: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                color="text.secondary"
              >
                {active.map((control) => `${control.label}: ${control.summary ?? 'Set'}`).join(' · ')}
              </Typography>
            ) : null}
            <Collapse in={open}>
              <Stack id={regionId} spacing={0.75} sx={{ pb: 1, pl: 0.5 }}>
                {shown.map((control) => (
                  <React.Fragment key={control.id}>{control.node}</React.Fragment>
                ))}
                {hidden > 0 || more.includes(group.id) ? (
                  <Button
                    onClick={() =>
                      setMore((current) =>
                        current.includes(group.id)
                          ? current.filter((entry) => entry !== group.id)
                          : [...current, group.id],
                      )
                    }
                    size="small"
                    sx={{ alignSelf: 'flex-start', fontSize: 11, px: 0.5, textTransform: 'none' }}
                  >
                    {more.includes(group.id) ? 'Show fewer' : `More ${group.label.toLowerCase()} filters (${hidden})`}
                  </Button>
                ) : null}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
      {!controls.some((control) => control.label.toLowerCase().includes(match.trim().toLowerCase())) ? (
        <Typography color="text.secondary" sx={{ py: 2, fontSize: 12 }}>
          No matching filters.
        </Typography>
      ) : null}
    </Box>
  );
}
